import { LocalizedLink } from "@/components/LocalizedLink";
import { Globe, Shield, Users, Plane, FileCheck, Scale, AlertTriangle, ArrowRight, Languages, Clock, CheckCircle } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CTASection from "@/components/CTASection";
import QuickContact from "@/components/QuickContact";
import { useLanguage } from "@/hooks/useLanguage";
import { RelatedLinks } from "@/components/RelatedLinks";

import immigrationHero from "@/assets/immigration-hero.jpg";

const ImmigrationServices = () => {
  const { t, language } = useLanguage();

  const content = {
    en: {
      hero: {
        title: "Immigration Services",
        subtitle: "Comprehensive immigration legal services to help you and your family navigate the U.S. immigration system. From visas to citizenship, we're here to guide you every step of the way.",
        schedule: "Schedule Consultation",
        call: "Call"
      },
      services: {
        title: "Our Immigration Services",
        subtitle: "From asylum to citizenship, we handle all aspects of immigration law with expertise and compassion.",
        items: [
          {
            title: "Asylum in the United States",
            description: "Protecting those fleeing persecution based on race, religion, nationality, political opinion, or membership in a particular social group."
          },
          {
            title: "Citizenship & Naturalization",
            description: "Guidance through the naturalization process to become a U.S. citizen, including application preparation and interview support."
          },
          {
            title: "Family-Based Immigration",
            description: "Helping families reunite through green card petitions for spouses, children, parents, and siblings."
          },
          {
            title: "Work Visas & Employment Immigration",
            description: "H-1B, L-1, O-1, E-2, and EB-2/EB-3 employment-based immigration for professionals and investors."
          },
          {
            title: "Deportation/Removal Defense",
            description: "Aggressive defense against deportation, including bond hearings, appeals, and relief applications."
          },
          {
            title: "Waivers (I-601, I-601A, I-212)",
            description: "Overcoming inadmissibility grounds including unlawful presence, criminal convictions, and fraud."
          }
        ]
      },
      additional: {
        title: "Additional Immigration Services",
        items: [
          {
            title: "DACA - Deferred Action for Childhood Arrivals",
            description: "Initial applications, renewals, and advance parole for Dreamers."
          },
          {
            title: "K-1 Fiancé(e) Visa",
            description: "Bringing your fiancé(e) to the U.S. for marriage and adjustment of status."
          },
          {
            title: "VAWA Petitions",
            description: "Self-petitions for abused spouses, children, and parents of U.S. citizens/residents."
          },
          {
            title: "Humanitarian Parole & TPS",
            description: "Temporary protected status and humanitarian parole for emergency situations."
          },
          {
            title: "Green Card Renewal",
            description: "Form I-90 to renew or replace your permanent resident card."
          },
          {
            title: "Consular Processing",
            description: "Immigrant visa processing through U.S. embassies and consulates abroad."
          }
        ]
      },
      quickContact: {
        title: "Ready to Start Your Immigration Process?",
        description: "Contact us today for a free consultation. Our experienced attorneys are ready to help you."
      },
      faq: {
        title: "Frequently Asked Questions",
        items: [
          {
            question: "How long does the immigration process take?",
            answer: "Processing times vary significantly depending on the type of case and USCIS workload. Family-based green cards can take 6-24 months, while asylum cases may take 1-3 years. Employment-based cases vary from 3-18 months. We monitor your case closely and keep you updated throughout."
          },
          {
            question: "Do I need a lawyer for my immigration case?",
            answer: "While not required by law, having an experienced immigration attorney significantly increases your chances of success. Immigration law is complex and constantly changing. A mistake on your application can lead to denials, delays, or even deportation. Our expertise helps avoid these pitfalls."
          },
          {
            question: "What if I'm facing deportation?",
            answer: "If you've received a Notice to Appear (NTA) or are in removal proceedings, contact us immediately. Time is critical in deportation cases. We can explore relief options including asylum, cancellation of removal, adjustment of status, or appeals. Don't wait - call us now."
          },
          {
            question: "Can I work while my immigration case is pending?",
            answer: "It depends on your case type and current status. Some applicants can obtain work permits (EAD) while their green card or asylum application is pending. DACA recipients can renew work authorization. We'll assess your eligibility and file for work authorization if possible."
          },
          {
            question: "What's the difference between a visa and a green card?",
            answer: "A visa is temporary permission to enter/stay in the U.S. for a specific purpose (tourism, work, study). A green card (permanent residence) allows you to live and work permanently in the U.S. and eventually apply for citizenship. We handle both temporary visas and permanent residence applications."
          }
        ]
      },
      why: {
        title: "Why Choose Us for Immigration Services?",
        items: [
          {
            title: "Trilingual Excellence",
            description: "Full legal services in English, Spanish, and Vietnamese - we understand your language and your culture."
          },
          {
            title: "Prompt Response",
            description: "Quick response during business hours for urgent immigration matters."
          },
          {
            title: "Proven Success Record",
            description: "Years of experience with successful outcomes in even the most complex immigration cases."
          }
        ]
      },
      cta: {
        title: "Ready to Start Your Immigration Journey?",
        description: "Contact Daniel Albert Law Firm today for a consultation. Let us help you achieve your American dream."
      }
    },
    es: {
      hero: {
        title: "Servicios de Inmigración",
        subtitle: "Servicios legales de inmigración integrales para ayudarle a usted y a su familia a navegar el sistema de inmigración de EE.UU. Desde visas hasta ciudadanía, estamos aquí para guiarle en cada paso del camino.",
        schedule: "Agendar Consulta",
        call: "Llamar"
      },
      services: {
        title: "Nuestros Servicios de Inmigración",
        subtitle: "Desde asilo hasta ciudadanía, manejamos todos los aspectos de la ley de inmigración con experiencia y compasión.",
        items: [
          {
            title: "Asilo en Estados Unidos",
            description: "Protegiendo a quienes huyen de persecución por raza, religión, nacionalidad, opinión política o pertenencia a un grupo social particular."
          },
          {
            title: "Ciudadanía y Naturalización",
            description: "Orientación a través del proceso de naturalización para convertirse en ciudadano estadounidense, incluyendo preparación de solicitud y apoyo en entrevistas."
          },
          {
            title: "Inmigración Basada en Familia",
            description: "Ayudando a las familias a reunirse mediante peticiones de tarjeta verde para cónyuges, hijos, padres y hermanos."
          },
          {
            title: "Visas de Trabajo e Inmigración Laboral",
            description: "H-1B, L-1, O-1, E-2 y EB-2/EB-3 inmigración basada en empleo para profesionales e inversionistas."
          },
          {
            title: "Defensa Contra Deportación/Remoción",
            description: "Defensa agresiva contra la deportación, incluyendo audiencias de fianza, apelaciones y solicitudes de alivio."
          },
          {
            title: "Perdones (I-601, I-601A, I-212)",
            description: "Superando causales de inadmisibilidad incluyendo presencia ilegal, condenas penales y fraude."
          }
        ]
      },
      additional: {
        title: "Servicios Adicionales de Inmigración",
        items: [
          {
            title: "DACA - Acción Diferida para Llegados en la Infancia",
            description: "Solicitudes iniciales, renovaciones y permiso anticipado para Dreamers."
          },
          {
            title: "Visa K-1 de Prometido(a)",
            description: "Trayendo a su prometido(a) a EE.UU. para matrimonio y ajuste de estatus."
          },
          {
            title: "Peticiones VAWA",
            description: "Auto-peticiones para cónyuges, hijos y padres maltratados de ciudadanos/residentes de EE.UU."
          },
          {
            title: "Permiso Humanitario y TPS",
            description: "Estado de protección temporal y permiso humanitario para situaciones de emergencia."
          },
          {
            title: "Renovación de Tarjeta Verde",
            description: "Formulario I-90 para renovar o reemplazar su tarjeta de residente permanente."
          },
          {
            title: "Procesamiento Consular",
            description: "Procesamiento de visa de inmigrante a través de embajadas y consulados de EE.UU. en el extranjero."
          }
        ]
      },
      quickContact: {
        title: "¿Listo para Comenzar su Proceso de Inmigración?",
        description: "Contáctenos hoy para una consulta gratuita. Nuestros abogados experimentados están listos para ayudarle."
      },
      faq: {
        title: "Preguntas Frecuentes",
        items: [
          {
            question: "¿Cuánto tiempo toma el proceso de inmigración?",
            answer: "Los tiempos de procesamiento varían significativamente según el tipo de caso y la carga de trabajo de USCIS. Las tarjetas verdes basadas en familia pueden tomar 6-24 meses, mientras que los casos de asilo pueden tomar 1-3 años. Los casos basados en empleo varían de 3-18 meses. Monitoreamos su caso de cerca y lo mantenemos actualizado."
          },
          {
            question: "¿Necesito un abogado para mi caso de inmigración?",
            answer: "Aunque no es requerido por ley, tener un abogado de inmigración experimentado aumenta significativamente sus posibilidades de éxito. La ley de inmigración es compleja y cambia constantemente. Un error en su solicitud puede llevar a denegaciones, retrasos o incluso deportación. Nuestra experiencia ayuda a evitar estos problemas."
          },
          {
            question: "¿Qué pasa si enfrento deportación?",
            answer: "Si ha recibido un Aviso de Comparecencia (NTA) o está en procedimientos de remoción, contáctenos inmediatamente. El tiempo es crítico en casos de deportación. Podemos explorar opciones de alivio incluyendo asilo, cancelación de remoción, ajuste de estatus o apelaciones. No espere - llámenos ahora."
          },
          {
            question: "¿Puedo trabajar mientras mi caso de inmigración está pendiente?",
            answer: "Depende del tipo de caso y su estatus actual. Algunos solicitantes pueden obtener permisos de trabajo (EAD) mientras su solicitud de tarjeta verde o asilo está pendiente. Los beneficiarios de DACA pueden renovar la autorización de trabajo. Evaluaremos su elegibilidad y presentaremos la solicitud de autorización de trabajo si es posible."
          },
          {
            question: "¿Cuál es la diferencia entre una visa y una tarjeta verde?",
            answer: "Una visa es permiso temporal para entrar/permanecer en EE.UU. para un propósito específico (turismo, trabajo, estudio). Una tarjeta verde (residencia permanente) le permite vivir y trabajar permanentemente en EE.UU. y eventualmente solicitar la ciudadanía. Manejamos tanto visas temporales como solicitudes de residencia permanente."
          }
        ]
      },
      why: {
        title: "¿Por Qué Elegirnos para Servicios de Inmigración?",
        items: [
          {
            title: "Excelencia Trilingüe",
            description: "Servicios legales completos en inglés, español y vietnamita - entendemos su idioma y su cultura."
          },
          {
            title: "Respuesta Rápida",
            description: "Respuesta rápida durante el horario de oficina para asuntos urgentes de inmigración."
          },
          {
            title: "Historial de Éxito Comprobado",
            description: "Años de experiencia con resultados exitosos incluso en los casos de inmigración más complejos."
          }
        ]
      },
      cta: {
        title: "¿Listo para Comenzar su Viaje de Inmigración?",
        description: "Contacte a Daniel Albert Law Firm hoy para una consulta. Permítanos ayudarle a alcanzar su sueño americano."
      }
    },
    vi: {
      hero: {
        title: "Dịch Vụ Di Trú",
        subtitle: "Dịch vụ pháp lý di trú toàn diện để giúp bạn và gia đình điều hướng hệ thống di trú Hoa Kỳ. Từ visa đến quốc tịch, chúng tôi ở đây để hướng dẫn bạn từng bước.",
        schedule: "Đặt Lịch Tư Vấn",
        call: "Gọi"
      },
      services: {
        title: "Dịch Vụ Di Trú Của Chúng Tôi",
        subtitle: "Từ tị nạn đến quốc tịch, chúng tôi xử lý tất cả các khía cạnh của luật di trú với chuyên môn và lòng thương.",
        items: [
          {
            title: "Tị Nạn tại Hoa Kỳ",
            description: "Bảo vệ những người chạy trốn bức hại dựa trên chủng tộc, tôn giáo, quốc tịch, quan điểm chính trị hoặc thành viên của nhóm xã hội cụ thể."
          },
          {
            title: "Quốc Tịch & Nhập Tịch",
            description: "Hướng dẫn qua quy trình nhập tịch để trở thành công dân Hoa Kỳ, bao gồm chuẩn bị đơn và hỗ trợ phỏng vấn."
          },
          {
            title: "Di Trú Dựa Trên Gia Đình",
            description: "Giúp các gia đình đoàn tụ thông qua đơn xin thẻ xanh cho vợ/chồng, con cái, cha mẹ và anh chị em."
          },
          {
            title: "Visa Làm Việc & Di Trú Việc Làm",
            description: "H-1B, L-1, O-1, E-2 và EB-2/EB-3 di trú dựa trên việc làm cho chuyên gia và nhà đầu tư."
          },
          {
            title: "Phòng Thủ Trục Xuất/Di Dời",
            description: "Phòng thủ quyết liệt chống trục xuất, bao gồm phiên tòa tại ngoại, kháng cáo và đơn xin cứu trợ."
          },
          {
            title: "Miễn Trừ (I-601, I-601A, I-212)",
            description: "Vượt qua lý do không được nhập cảnh bao gồm hiện diện bất hợp pháp, án tù hình sự và gian lận."
          }
        ]
      },
      additional: {
        title: "Dịch Vụ Di Trú Bổ Sung",
        items: [
          {
            title: "DACA - Hoãn Trục Xuất cho Người Đến Khi Còn Nhỏ",
            description: "Đơn ban đầu, gia hạn và giấy phép trước cho Dreamers."
          },
          {
            title: "Visa K-1 Hôn Phu/Thê",
            description: "Đưa hôn phu/thê của bạn đến Hoa Kỳ để kết hôn và điều chỉnh tình trạng."
          },
          {
            title: "Đơn VAWA",
            description: "Tự nộp đơn cho vợ/chồng, con và cha mẹ bị lạm dụng của công dân/thường trú nhân Hoa Kỳ."
          },
          {
            title: "Tại Ngoại Nhân Đạo & TPS",
            description: "Tình trạng được bảo vệ tạm thời và tại ngoại nhân đạo cho tình huống khẩn cấp."
          },
          {
            title: "Gia Hạn Thẻ Xanh",
            description: "Mẫu I-90 để gia hạn hoặc thay thế thẻ thường trú nhân của bạn."
          },
          {
            title: "Xử Lý Lãnh Sự",
            description: "Xử lý visa nhập cư thông qua đại sứ quán và lãnh sự quán Hoa Kỳ ở nước ngoài."
          }
        ]
      },
      quickContact: {
        title: "Sẵn Sàng Bắt Đầu Quy Trình Di Trú Của Bạn?",
        description: "Liên hệ với chúng tôi ngay hôm nay để được tư vấn miễn phí. Các luật sư giàu kinh nghiệm của chúng tôi sẵn sàng giúp bạn."
      },
      faq: {
        title: "Câu Hỏi Thường Gặp",
        items: [
          {
            question: "Quy trình di trú mất bao lâu?",
            answer: "Thời gian xử lý khác nhau đáng kể tùy thuộc vào loại trường hợp và khối lượng công việc của USCIS. Thẻ xanh dựa trên gia đình có thể mất 6-24 tháng, trong khi các vụ tị nạn có thể mất 1-3 năm. Các trường hợp dựa trên việc làm khác nhau từ 3-18 tháng. Chúng tôi theo dõi chặt chẽ trường hợp của bạn và cập nhật thông tin cho bạn."
          },
          {
            question: "Tôi có cần luật sư cho vụ di trú của mình không?",
            answer: "Mặc dù không bắt buộc theo pháp luật, việc có luật sư di trú giàu kinh nghiệm tăng đáng kể cơ hội thành công của bạn. Luật di trú phức tạp và liên tục thay đổi. Một sai lầm trong đơn của bạn có thể dẫn đến từ chối, trì hoãn hoặc thậm chí trục xuất. Chuyên môn của chúng tôi giúp tránh những vấn đề này."
          },
          {
            question: "Nếu tôi đối mặt với trục xuất thì sao?",
            answer: "Nếu bạn đã nhận Thông Báo Xuất Hiện (NTA) hoặc đang trong thủ tục di dời, hãy liên hệ với chúng tôi ngay lập tức. Thời gian rất quan trọng trong các vụ trục xuất. Chúng tôi có thể khám phá các lựa chọn cứu trợ bao gồm tị nạn, hủy bỏ di dời, điều chỉnh tình trạng hoặc kháng cáo. Đừng chờ - gọi cho chúng tôi ngay."
          },
          {
            question: "Tôi có thể làm việc trong khi vụ di trú của tôi đang chờ xử lý không?",
            answer: "Tùy thuộc vào loại vụ và tình trạng hiện tại của bạn. Một số người nộp đơn có thể nhận được giấy phép làm việc (EAD) trong khi đơn xin thẻ xanh hoặc tị nạn của họ đang chờ xử lý. Người nhận DACA có thể gia hạn giấy phép làm việc. Chúng tôi sẽ đánh giá tư cách của bạn và nộp đơn xin giấy phép làm việc nếu có thể."
          },
          {
            question: "Sự khác biệt giữa visa và thẻ xanh là gì?",
            answer: "Visa là sự cho phép tạm thời để nhập cảnh/ở lại Hoa Kỳ cho một mục đích cụ thể (du lịch, làm việc, học tập). Thẻ xanh (thường trú) cho phép bạn sống và làm việc vĩnh viễn tại Hoa Kỳ và cuối cùng xin quốc tịch. Chúng tôi xử lý cả visa tạm thời và đơn xin thường trú."
          }
        ]
      },
      why: {
        title: "Tại Sao Chọn Chúng Tôi Cho Dịch Vụ Di Trú?",
        items: [
          {
            title: "Xuất Sắc Ba Ngôn Ngữ",
            description: "Dịch vụ pháp lý đầy đủ bằng tiếng Anh, tiếng Tây Ban Nha và tiếng Việt - chúng tôi hiểu ngôn ngữ và văn hóa của bạn."
          },
          {
            title: "Phản Hồi Nhanh",
            description: "Phản hồi nhanh trong giờ làm việc cho các vấn đề di trú khẩn cấp."
          },
          {
            title: "Thành Tích Thành Công Đã Được Chứng Minh",
            description: "Nhiều năm kinh nghiệm với kết quả thành công ngay cả trong các vụ di trú phức tạp nhất."
          }
        ]
      },
      cta: {
        title: "Sẵn Sàng Bắt Đầu Hành Trình Di Trú Của Bạn?",
        description: "Liên hệ với Daniel Albert Law Firm ngay hôm nay để được tư vấn. Hãy để chúng tôi giúp bạn đạt được giấc mơ Mỹ."
      }
    }
  };

  const pageContent = content[language];
  
  const services = [
    {
      icon: Shield,
      href: "/practice-areas/immigration-services/asylum-in-the-united-states",
    },
    {
      icon: FileCheck,
      href: "/practice-areas/immigration-services/citizenship-naturalization",
    },
    {
      icon: Users,
      href: "/practice-areas/immigration-services/family-based-immigration",
    },
    {
      icon: Plane,
      href: "/practice-areas/immigration-services/employment-based-immigration",
    },
    {
      icon: AlertTriangle,
      href: "/practice-areas/immigration-services/deportation-removal-defense",
    },
    {
      icon: Scale,
      href: "/practice-areas/immigration-services/waivers-i601-i601a-i212",
    },
  ];

  const additionalServicesRoutes = [
    "/practice-areas/immigration-services/deferred-action-for-childhood-arrivals",
    "/practice-areas/immigration-services/k-1-fiance-visa",
    "/practice-areas/immigration-services/vawa-petitions",
    "/practice-areas/immigration-services/humanitarian-parole-tps",
    "/practice-areas/immigration-services/green-card-renewal",
    "/practice-areas/immigration-services/consular-processing",
  ];

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="relative h-[600px] flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${immigrationHero})`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary-dark/95 via-primary/90 to-primary-dark/95"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-primary-foreground">
            <Globe className="h-20 w-20 mx-auto mb-6 text-accent drop-shadow-lg" />
            <h1 className="text-5xl md:text-6xl font-bold mb-6 drop-shadow-lg">{pageContent.hero.title}</h1>
            <p className="text-xl opacity-95 mb-8 drop-shadow-md">
              {pageContent.hero.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" variant="hero">
                <LocalizedLink to="/appointment">{pageContent.hero.schedule}</LocalizedLink>
              </Button>
              <a href="tel:8329303059">
                <Button size="lg" variant="outline" className="border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary-dark bg-white/10 backdrop-blur-sm">
                  {pageContent.hero.call}: (832) 930-3059
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>


      {/* Services Grid */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">{pageContent.services.title}</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {pageContent.services.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <Card key={index} className="hover:shadow-xl transition-all hover-scale duration-300 bg-card/80 backdrop-blur-sm border-accent/20">
                <CardHeader>
                  <div className="h-12 w-12 rounded-lg bg-gradient-gold-shine flex items-center justify-center mb-4 shadow-gold">
                    <service.icon className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="text-xl">{pageContent.services.items[index].title}</CardTitle>
                  <CardDescription>{pageContent.services.items[index].description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button asChild variant="link" className="p-0 text-accent hover:text-accent/80">
                    <LocalizedLink to={service.href}>
                      {t('cta.learn')} <ArrowRight className="ml-1 h-4 w-4" />
                    </LocalizedLink>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center">{pageContent.additional.title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {pageContent.additional.items.map((item, index) => (
                <div key={index} className="bg-card p-6 rounded-lg shadow-md hover-scale transition-all">
                  <h3 className="text-lg font-bold mb-3">{item.title}</h3>
                  <p className="text-sm text-muted-foreground mb-3">{item.description}</p>
                  <LocalizedLink to={additionalServicesRoutes[index]} className="text-accent hover:underline text-sm font-medium">
                    {t('cta.learn')} →
                  </LocalizedLink>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quick Contact Section */}
      <section className="py-16 bg-gradient-subtle">
        <div className="container mx-auto px-4">
          <QuickContact 
            title={pageContent.quickContact.title}
            description={pageContent.quickContact.description}
          />
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center">{pageContent.faq.title}</h2>
            <Accordion type="single" collapsible className="w-full">
              {pageContent.faq.items.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">{pageContent.why.title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div>
                <div className="h-16 w-16 rounded-full bg-gradient-gold-shine flex items-center justify-center mx-auto mb-4 shadow-gold">
                  <Languages className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-bold text-lg mb-2">{pageContent.why.items[0].title}</h3>
                <p className="text-sm text-muted-foreground">
                  {pageContent.why.items[0].description}
                </p>
              </div>
              <div>
                <div className="h-16 w-16 rounded-full bg-gradient-gold-shine flex items-center justify-center mx-auto mb-4 shadow-gold">
                  <Clock className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-bold text-lg mb-2">{pageContent.why.items[1].title}</h3>
                <p className="text-sm text-muted-foreground">
                  {pageContent.why.items[1].description}
                </p>
              </div>
              <div>
                <div className="h-16 w-16 rounded-full bg-gradient-gold-shine flex items-center justify-center mx-auto mb-4 shadow-gold">
                  <CheckCircle className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-bold text-lg mb-2">{pageContent.why.items[2].title}</h3>
                <p className="text-sm text-muted-foreground">
                  {pageContent.why.items[2].description}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <RelatedLinks category="immigration" />

      <CTASection
        title={pageContent.cta.title}
        description={pageContent.cta.description}
      />

      <Footer />
    </div>
  );
};

export default ImmigrationServices;
